type FlushResult = {
  consumed: boolean;
};

/**
 * Buffers incoming calls and flushes them in batches every `delay` ms.
 *
 * @template T
 * @param delay - Throttle interval in milliseconds
 * @param fn - Function that receives buffered inputs
 * @returns Buffered/throttled function
 */
export function createBufferedThrottle<T>(
  delay: number,
  fn: (batchedArgs: T[]) => FlushResult
): (input: T) => void {
  const queue: T[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    timer = null;

    if (queue.length === 0) return;

    const current = queue.slice();

    const result = fn(current);

    if (result.consumed) {
      queue.splice(0, current.length);
    }

    if (queue.length > 0) {
      timer = setTimeout(flush, delay);
    }
  };

  return function bufferedThrottle(input: T): void {
    queue.push(input);

    if (timer) return;

    timer = setTimeout(flush, delay);
  };
}
